import Field from '../Field/Field'
import PropTypes from 'prop-types'
import React, { Component, Fragment } from 'react'
import { Prompt } from 'react-router'
import Spinner from '../Spinner/Spinner'
import get from 'lodash-es/get'
import set from 'lodash-es/set'
import merge from 'lodash-es/merge'
import Card from './../Card/Card'
import { CardBody, CardHeader } from '../Card/CardElements'
import Label from './../Label/Label'
import IconComponent from '../Icon/Icon'
import Button from '../Button/Button'
import { observer } from 'mobx-react'
import { computed, makeObservable } from 'mobx'
import { FormCellStyles, noCellStyles } from './FormCellStyles'
import debounce from 'lodash-es/debounce'
import { countWords } from './utils'
import { boundCheck, boundError } from '../../utility/min-max'
import { conditionByType } from '@polystack/components/src/utility/fieldErrorCheck'
import Popover from '../Popover/Popover'


const BUTTON_POSITIONS = ['hidden', 'top-left', 'top-centered', 'top-right', 'bottom-left', 'bottom-centered', 'bottom-right']
const BORDER_STYLES = ['dotted', 'dashed', 'solid', 'none']
const ALIGN = ['center', 'start', 'end']
const TRANSFORM = ['lowercase', 'uppercase', 'capitalize', 'none', 'inherit']
const STYLES = ['normal', 'italic', 'oblique']
const WEIGHT = ['normal', 'bold', 'bolder']
export const ICON_SIZE = ['lg', '2x', '3x', '4x', '5x']
const NO_SAVE_METHOD_ERROR_MESSAGE = 'Trying to save but no save function defined. Please contact an administrator.'
const UNSAVED_PROMPT_MESSAGE = 'You have unsaved changes - are you sure you want to continue?'
const SAVE_ERROR_MESSAGE = 'Saving failed!'

class FieldSet { }

/**
 * Form component
 */
const Form = observer(class Form extends Component {
  get _newRecord () {
    return this.props.newRecord && this.props.newRecord.$ ? this.props.newRecord.$.snapshot : { ...(this.props.newRecord || {}) }
  }

  get _record () {
    return this.props.record && this.props.record.$ ? this.props.record.$.snapshot : { ...(this.props.record || {}) }
  }

  get record () {
    const combined = merge({}, this._newRecord, this._record, this.state.record)
    if (this.props.newRecord && this.props.newRecord.$) return this.props.newRecord.$.clone(combined)
    if (this.props.record && this.props.record.$) return this.props.record.$.clone(combined)
    return combined
  }

  get recordWithoutState () {
    const combined = { ...this._newRecord, ...this._record }
    if (this.props.newRecord && this.props.newRecord.$) return this.props.newRecord.$.clone(combined)
    if (this.props.record && this.props.record.$) return this.props.record.$.clone(combined)
    return combined
  }

  toast = this.props.ctx && this.props.ctx.notify
  state = {
    record: {},
    actionButtons: false,
    recordSaved: false,
    preventSubmit: false,
    clear: async () => {
      this.setState({ record: {} })
    },
    setRecord: async (record = {}) => {
      this.setState({ recordSaved: true, actionButtons: false, record })
      return record
    },
    errors: {},
    setError: (fieldPath, errorMessage) => {
      this.setState({
        errors: {
          ...this.state.errors,
          [fieldPath]: errorMessage
        }
      })
    },
    loading: false,
    saveError: false
  }

  constructor (props) {
    super(props)

    makeObservable(this, {
      _newRecord: computed,
      _record: computed,
      record: computed,
      recordWithoutState: computed
    })

    this.cancelChanges = this.cancelChanges.bind(this)
    this.onSave = this.onSave.bind(this)
    this.formRef = React.createRef()
    this.fieldRefs = new Map()
  }

  componentDidMount () {
    const { parentNode } = (this.formRef && this.formRef.current) || {}
    const { formStyles } = this.props.styling || {}
    if (parentNode.style) parentNode.style.boxShadow = (formStyles && formStyles.shadow) || ''
    if (parentNode.style) parentNode.style.borderRadius = (formStyles && formStyles.borderRadius) || ''
  }

  cancelChanges () {
    const { showAbortMessage, abortMessageText } = this.props.toastMessages || {}
    this.setState({
      record: {},
      actionButtons: false
    })
    if (this.props.onCancel && typeof this.props.onCancel === 'function') {
      this.props.onCancel()
    }
    if (showAbortMessage) {
      this.toast && this.toast.warning(abortMessageText || 'Aborting..', { autoClose: 2000 })
    }
  }

  async onSave () {
    const { preventSubmit } = this.state
    if (preventSubmit) return
    const { save, toastMessages } = this.props
    const { showSuccessMessage, successMessageText, showErrorMessage, errorMessageText, includeThrownError } = toastMessages || {}
    if (typeof save !== 'function') throw new Error(NO_SAVE_METHOD_ERROR_MESSAGE)
    this.setState({ actionButtons: false })
    try {
      const rec = { ...this._newRecord, ...this.state.record }
      await save(rec, this.recordWithoutState)
      if (showSuccessMessage) {
        this.toast && this.toast.success(successMessageText || 'Record updated', { autoClose: 2000 })
      }
      this.setState({ record: {} })
    } catch (e) {
      if (showErrorMessage) {
        this.toast && this.toast.error(errorMessageText || `Error ${includeThrownError ? `: ${e}` : ''}`, { autoClose: 5000 })
      }
    }
  }

  saveOnChange = debounce(async () => {
    const { showErrorMessage, errorMessageText, includeThrownError } = this.props.toastMessages || {}
    try {
      this.setState({
        loading: true,
        saveError: false
      })
      const rec = { ...this._newRecord, ...this.state.record }
      await this.props.save(rec, this.recordWithoutState)
      this.setState({
        loading: false
      })
      // this.setState({ record: {} })
    } catch (e) {
      if (showErrorMessage) {
        this.toast && this.toast.error(errorMessageText || `Error ${includeThrownError ? `: ${e}` : ''}`, { autoClose: 5000 })
      }
      this.setState({
        loading: false,
        saveError: true
      })
    }
  }, 100)

  setFieldValue = async (fieldPath, value) => {
    const record = set({ ...this.state.record }, fieldPath, value)
    this.setState({
      actionButtons: this.isFormDirty(record),
      record
    })
  }

  onFieldValueChange = async (fieldPath, type, value) => {
    const { saveOnChange, save, onFormFieldChange } = this.props
    if (typeof save !== 'function') throw new Error(NO_SAVE_METHOD_ERROR_MESSAGE)
    await this.setFieldValue(fieldPath, value)
    if (saveOnChange) {
      this.saveOnChange()
    }
    if (onFormFieldChange && typeof onFormFieldChange === 'function') {
      onFormFieldChange(fieldPath, type, value, this.setFieldValue)
    }
  }

  isFormDirty = (stateRecord) => {
    const { fieldset } = this.props
    return fieldset.some(field => {
      if (!stateRecord[field.fieldPath] || !this._record[field.fieldPath]) {
        return !!stateRecord[field.fieldPath] !== !!this._record[field.fieldPath]
      }
      return stateRecord[field.fieldPath] !== this._record[field.fieldPath]
    })
  }

  checkIsFormValid = () => {
    let errorList = this.errorsByType('all')
    return errorList ? !Object.values(this.errorsByType('all')).some(v => v === true) : true
  }

  errorsByType = (type = 'all') => {
    let condition = conditionByType(type)
    const { fieldset } = this.props
    if (!fieldset) return
    return fieldset.reduce((o, f) => {
        let value = this.record[f.fieldPath]
        let error = condition(f, value)
        o[f.fieldPath] = error
        return o
      }, {})
  }

  setPreventSubmit = (preventSubmit) => {
    this.setState({
      preventSubmit
    })
  }

  render () {
    const { actionButtons, preventSubmit, loading, saveError } = this.state
    const {
      label,
      isLoading,
      saveOnChange,
      buttonsPosition,
      buttonsAlwaysVisible,
      showCancelButton,
      fieldsSpans,
      formHeader,
      formLayout,
      emitState,
      history,
      preventUnsavedRedirect,
      autoSave: {
        autoSaveFeedback,
        autoSaveFeedbackPosition,
        autoSaveFeedbackLoaderColor,
      } = {},
      styling: {
        formStyles,
        fieldStyles,
        helpTextTools,
        hintTextStyles
      } = {}
    } = this.props
    const isFormValid = this.checkIsFormValid() && !preventSubmit
    emitState && emitState({ isFormValid, ...this.state })
    const buttonsVerticalAlign = buttonsPosition[0] === 't' ? 'top' : 'bottom'
    const loadingStyle = isLoading ? { height: '100%', display: 'flex', justifyContent: 'center' } : { height: '100%' }
    /** Replaces newRecord fields with bound record ones */
    const canSave = typeof this.props.save === 'function'
    const hasButtons = buttonsAlwaysVisible || (canSave && !saveOnChange && buttonsPosition !== 'hidden')
    let cols = this.props.columns
    if (!cols >= 1) cols = 1
    const fields = this.props.fieldset && this.props.fieldset.map((f, index) => {
      let value = get(this.record, f.fieldPath)
      const isTextField = f.type === 'STRING' || f.type === 'TEXTAREA'
      const boundErrors = this.errorsByType('bound')
      if (!this.fieldRefs.get(f.fieldPath)) {
        this.fieldRefs.set(f.fieldPath, React.createRef(null))
      }
      const ref = this.fieldRefs.get(f.fieldPath)
      return (
        <div className={'field-container mx-1'} key={index}>
          <Field
            {...f}
            {...f.isHtmlFormatted && { imageUploadFunc: this.props.imageUploadFunc }}
            {...f.isHtmlFormatted && { imageUploadPath: this.props.imageUploadPath }}
            {...f.isHtmlFormatted && { mentionGroup: this.props.mentionGroup }}
            {...f.isHtmlFormatted && { mentionField: this.props.mentionField }}
            {...f.isHtmlFormatted && { cmpId: this.props.cmpId }}
            inputRef={ref}
            record={this.record}
            wrapper
            ctx={this.props.ctx}
            isOutput={!f.editable}
            onValueChange={this.onFieldValueChange.bind(this, f.fieldPath, f.type)}
            value={value}
            error={this.state.errors[f.fieldPath] || conditionByType('all')(f, value)}
            helpTextTools={helpTextTools}
            isFormField
            preventSubmit={this.setPreventSubmit}
            history={history}
            {...isTextField && { words: countWords(value) }}
          />
          <Popover hostRef={ref} isOpen={!!boundErrors[f.fieldPath]} whenFocused whenHovered>
            <h5>Value Out of Bounds</h5>
            <p>The value entered needs to be {f.minNum !== null ? 'above or equal to'  : ''} <b>{f.minNum}</b> {f.minNum !== null && f.maxNum !== null ? 'and' : ''} {f.maxNum !== null ? 'below or equal to' : ''} <b>{f.maxNum}</b></p>
          </Popover>
        </div>)
    })

    return (
      <Fragment>
        <div
          className={'form'}
          ref={this.formRef}
          style={{ ...loadingStyle }}>
          {
            isLoading
              ? <Spinner isVisible={isLoading} size='small' />
              : (
                <Card style={{
                  height: '100%',
                  background: formStyles && formStyles.backgroundColor,
                  borderWidth: formStyles && formStyles.borderWidth,
                  borderStyle: formStyles && formStyles.borderStyle,
                  borderColor: formStyles && formStyles.borderColor,
                  borderRadius: formStyles && formStyles.borderRadius
                }}>
                  {
                    label && formHeader &&
                    <CardHeader style={{
                      backgroundColor: (formStyles && formStyles.headerBackgroundColor) || '#fff',
                      borderTopLeftRadius: formStyles && formStyles.borderRadius,
                      borderTopRightRadius: formStyles && formStyles.borderRadius
                    }}>
                      <div className={'label-container'}>
                        <Label value={label} />
                      </div>
                    </CardHeader>
                  }
                  <CardBody style={{ padding: formStyles && formStyles.padding, overflowY: 'auto', position: 'relative' }}>
                    {
                      label && !formHeader &&
                      <div className={'label-container'}>
                        <Label value={label} />
                      </div>
                    }
                    {
                      hasButtons && buttonsVerticalAlign === 'top' &&
                      <ActionButtons
                        saveDisabled={this.props.saveDisabled}
                        onSave={this.onSave}
                        cancelChanges={this.cancelChanges}
                        actionButtons={buttonsAlwaysVisible ? true : actionButtons}
                        buttonsPosition={buttonsPosition}
                        formStyles={formStyles}
                        isFormValid={isFormValid}
                        showCancelButton={showCancelButton}
                      />
                    }
                    {
                      saveOnChange && autoSaveFeedback &&
                      <div style={{
                        position: 'absolute',
                        right: saveError ? '5px' : 0,
                        ...autoSaveFeedbackPosition === 'top' && { top: saveError ? '10px' : '15px' },
                        ...autoSaveFeedbackPosition === 'bottom' && { bottom: saveError ? '10px' : '15px' },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        {
                          loading &&
                          <Spinner color={autoSaveFeedbackLoaderColor} isVisible={loading} size='xx-small' />
                        }
                        {
                          saveError &&
                          <div className={'d-flex flex-column justify-center align-items-center'}>
                            <IconComponent icon={'alert-triangle'} style={{ fontSize: '.9rem' }} styling={{ iconColor: '#dc3545' }} />
                            <small style={{ fontSize: '.5rem', color: '#dc3545', marginTop: '2px' }}>{SAVE_ERROR_MESSAGE}</small>
                          </div>
                        }
                      </div>
                    }
                    <Grid
                      fieldset={this.props.fieldset}
                      columns={cols}
                      gap={this.props.gap}
                      fieldsSpans={fieldsSpans}
                      fieldStyles={fieldStyles}
                      formLayout={formLayout}
                      hintTextStyles={hintTextStyles}
                      record={this.record}
                    >
                      {fields}
                    </Grid>
                    {
                      hasButtons && buttonsVerticalAlign === 'bottom' &&
                      <ActionButtons
                        saveDisabled={this.props.saveDisabled}
                        onSave={this.onSave}
                        cancelChanges={this.cancelChanges}
                        actionButtons={buttonsAlwaysVisible ? true : actionButtons}
                        buttonsPosition={buttonsPosition}
                        formStyles={formStyles}
                        isFormValid={isFormValid}
                        showCancelButton={showCancelButton}
                      />}
                  </CardBody>
                </Card>
                )
          }
        </div>
        {
          preventUnsavedRedirect &&
          <Prompt
            when={actionButtons}
            message={UNSAVED_PROMPT_MESSAGE}
          />
        }
        <style jsx>{`
          .form {
            padding-bottom: ${(formStyles && formStyles.padding) || '.5rem'};
            background: ${(formStyles && formStyles.backgroundColor) || '#fff'};
          }
          .form :global(.hint-text) {
            display: flex;
            justify-content: ${hintTextStyles && hintTextStyles.align === 'center' ? 'center' : `flex-${hintTextStyles && hintTextStyles.align}`};
            width: 100%;
            text-align: ${(hintTextStyles && hintTextStyles.align) || 'start'};
            font-size: ${(hintTextStyles && hintTextStyles.fontSize) || 'inherit'};
            color: ${(hintTextStyles && hintTextStyles.color) || 'inherit'};
            padding: ${(hintTextStyles && hintTextStyles.padding) || 0};
          }
          .label-container {
            color: ${(formStyles && formStyles.labelColor)} !important;
            text-transform: ${(formStyles && formStyles.labelTextTransform)};
            display: flex;
            justify-content: ${formStyles && formStyles.labelAlign === 'center' ? 'center' : `flex-${formStyles && formStyles.labelAlign}`};
          }
          :global(.label-container > div) {
            width: unset !important;
          }
          .actions-container {
            -webkit-transition: 1.5s;
            -moz-transition: 1.5s;
            -o-transition: 1.5s;
            transition: 1.5s;
            opacity: 0;
            visibility: hidden;
          }
          .actions-container.show {
            -webkit-transition: 1.5s;
            -moz-transition: 1.5s;
            -o-transition: 1.5s;
            transition: 1.5s;
            opacity: 1;
            visibility: visible;
          }
        `}</style>
      </Fragment>
    )
  }
})

const Grid = ({ columns, gap, fieldsSpans, children, fieldStyles, formLayout, hintTextStyles, fieldset, record }) => {
  const conditionalCellStyle = fieldStyles && !fieldStyles.labelSpacing ? FormCellStyles : noCellStyles
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gridGap: gap
    }}>
      {children && Array.isArray(children) && children.map((child, index) => {
        const {
          fieldPath,
          config = {},
          config: {
            charCount,
            charMaxCount,
            charLeft,
            wordCount,
            wordsMaxCount,
            wordsLeft
          } = {},
          editable,
          isHtmlFormatted
        } = fieldset[index]
        const fieldValue = record[fieldPath]
        const isCounting = charCount || wordCount
        const oldSpans = (fieldsSpans && fieldsSpans[fieldPath]) || {}
        const mergedSpans = Object.assign({}, oldSpans, config)
        let { colSpanStart, colSpanEnd, rowSpanWidth, spanned } = mergedSpans
        if (typeof colSpanStart !== 'number') colSpanStart = parseInt(colSpanStart, 10)
        if (typeof colSpanEnd !== 'number') colSpanEnd = parseInt(colSpanEnd, 10)
        if (typeof rowSpanWidth !== 'number') rowSpanWidth = parseInt(rowSpanWidth, 10)
        if (typeof columns !== 'number') columns = parseInt(columns, 10)
        if (rowSpanWidth > columns) rowSpanWidth = columns
        if (colSpanStart > columns) colSpanStart = rowSpanWidth
        if (colSpanEnd > columns) colSpanEnd = columns
        if (colSpanEnd === colSpanStart) rowSpanWidth = 1
        // let charsLeft = 0
        // if (config && config.charMaxCount) {
        //   charsLeft = parseInt(config.charMaxCount, 10) - ((fieldValue && fieldValue.length) || 0)
        // }

        return (
          <div
            className={`cell-width-${rowSpanWidth || 1}`}
            style={{
              overflow: 'hidden',
              justifySelf: 'stretch',
              alignSelf: 'stretch',
              padding: (fieldStyles && fieldStyles.cellPadding) || '.5rem .2rem',
              ...(spanned && columns > 1 && { gridColumnStart: colSpanStart }),
              ...(spanned && columns > 1 && { gridColumnEnd: colSpanEnd + 1 }),
              ...(spanned && columns > 1 && { width: `${(100 / (colSpanEnd + 1 - colSpanStart)) * rowSpanWidth}%` })
            }}
            key={index}
          >
            {child}
            {
              editable && !isHtmlFormatted && config && !!isCounting &&
              <span className={'hint-text'}>
                {
                  charCount && !charLeft &&
                  <small className={'pl-1 pr-1'}>{(fieldValue?.length) || 0} chars</small>
                }
                {/* { */}
                {/*   charLeft && charMaxCount && */}
                {/*   <small className={`pl-1 pr-1`}>{charsLeft} chars left</small> */}
                {/* } */}
                {
                  charLeft && charCount && charMaxCount &&
                  <small className={'pl-1 pr-1'}>{(fieldValue?.length) || 0} / {charMaxCount} chars</small>
                }
                {
                  wordCount && !wordsLeft &&
                  <small className={'pl-1 pr-1'}>{(wordCount) && countWords(fieldValue)} words</small>
                }
                {
                  wordCount && wordsLeft && wordsMaxCount &&
                  <small className={'pl-1 pr-1'}>{(wordCount) && countWords(fieldValue)} / {wordsMaxCount} words</small>
                }
              </span>
            }
          </div>
        )
      })
      }
      <style jsx>{`
      div :global(.react-select__control),
      div :global(.react-multi-select__control),
      div :global(.form-control) {
        background: ${fieldStyles && fieldStyles.backgroundColorEdit} !important;
        color: ${fieldStyles && fieldStyles.color} !important;
        text-transform: ${fieldStyles && fieldStyles.textTransform} !important;
        font-style: ${fieldStyles && fieldStyles.fontStyle} !important;
      }
      div :global(.output-wrapper .output-box span),
      div :global(.output-wrapper .output-box div) {
        cursor: default;
        color: ${fieldStyles && fieldStyles.colorOutput} !important;
      }
      div :global(.output-wrapper) {
        background: ${fieldStyles && fieldStyles.backgroundColorLocked} !important;
      }
      div :global(.output-wrapper .output-box) {
        padding-left: ${fieldStyles && fieldStyles.valueOffset ? `${fieldStyles.valueOffset} !important` : 'initial'};
      }
      div :global(.output-wrapper label) {
        padding-left: ${fieldStyles && fieldStyles.labelOffset ? `${fieldStyles.labelOffset} !important` : '2px'};
      }
      div :global(.react-select__control),
      div :global(.react-multi-select__control),
      div :global(.form-control) {
        border-width: ${fieldStyles && fieldStyles.borderWidth} !important;
        border-color: ${fieldStyles && fieldStyles.borderColor} !important;
        border-style: ${fieldStyles && fieldStyles.borderStyle} !important;
        border-radius: ${fieldStyles && fieldStyles.borderRadius} !important;
      }
      div :global(.input.is-invalid > .react-select__control),
      div :global(.input.is-invalid > .react-multi-select__control),
      div :global(.form-control.is-invalid) {
        border-color: ${(fieldStyles && fieldStyles.invalidBorderColor) || 'rgb(220, 53, 69)'} !important;
      }
      div :global(.input.is-invalid > .react-select__control:focus),
      div :global(.input.is-invalid > .react-multi-select__control:focus),
      div :global(.input.is-invalid > .react-select__control--is-focused),
      div :global(.input.is-invalid > .react-multi-select__control--is-focused),
      div :global(.form-control.is-invalid:focus) {
        box-shadow: 0 0 0 0.2rem ${convertToRgba(((fieldStyles && fieldStyles.invalidBorderColor) || 'rgb(220, 53, 69)'), 0.25)};
      }
      div :global(.react-select__control:focus),
      div :global(.react-multi-select__control:focus),
      div :global(.react-select__control--is-focused),
      div :global(.react-multi-select__control--is-focused),
      div :global(.form-control:focus) {
        background: ${fieldStyles && fieldStyles.focusedBackgroundColor};
        color: ${fieldStyles && fieldStyles.focusedColor};
        font-style: ${fieldStyles && fieldStyles.focusedFontStyle} !important;
        box-shadow: 0 0 0 0.2rem ${convertToRgba(((fieldStyles && fieldStyles.borderColor) || 'rgb(0, 123, 255)'), 0.25)};
      }
      div :global(.react-select__control:hover),
      div :global(.react-multi-select__control:hover),
      div :global(.form-control:hover) {
        border-color: ${fieldStyles && fieldStyles.borderColor};
        background: ${fieldStyles && fieldStyles.hoverBackgroundColor};
        color: ${fieldStyles && fieldStyles.hoverColor};
      }
      div :global(.react-select__multi-value__label),
      div :global(.react-multi-select__multi-value__label),
      div :global(.react-select__multi-value__remove),
      div :global(.react-multi-select__multi-value__remove) {
        background: ${fieldStyles && fieldStyles.multiSelectPillColor};
      }
      div :global(.react-multi-select__control) {
        max-height: ${fieldStyles && fieldStyles.allowMultiFieldsGrow ? 'none' : 'calc(1.75rem + 2px)'};
        overflow-y: ${fieldStyles && fieldStyles.allowMultiFieldsGrow ? 'hidden' : 'auto'};
      }
      div :global(.react-multi-select__control .react-multi-select__indicators) {
        align-items: ${fieldStyles && fieldStyles.allowMultiFieldsGrow ? 'center' : 'flex-start'};
      }
      div :global(.react-multi-select__control .react-multi-select__indicators div) {
        margin: ${fieldStyles && fieldStyles.allowMultiFieldsGrow ? 'auto' : '.25rem'};
      }
      div :global(label) {
        color: ${fieldStyles && fieldStyles.labelColor};
        font-size: ${fieldStyles && fieldStyles.labelFontSize};
        font-weight: ${fieldStyles && fieldStyles.labelFontWeight} !important;
        text-transform: ${fieldStyles && fieldStyles.labelTextTransform} !important;
      }
      div :global(.field-container .input-wrapper),
      div :global(.field-container .output .output-wrapper) {
        display: flex;
        flex-direction: ${formLayout === 'vertical' ? 'column' : 'row'};
      }
      div :global(.field-container .input-wrapper) {
        align-items: flex-start;
      }
      div :global(.field-container > .output > .output-wrapper) {
        align-items: ${formLayout === 'vertical' ? 'flex-start' : 'baseline'};
        width: 100%;
      }
      div :global(.field-container .field-label) {
        flex: 0 0 ${(fieldStyles && fieldStyles.labelSpacing) || 'auto'};
      }
    `}</style>
      <style jsx>{conditionalCellStyle}</style>
    </div>
  )
}

const ActionButtons = (props) => {
  const { saveDisabled, onSave, cancelChanges, actionButtons, buttonsPosition, formStyles, isFormValid, showCancelButton } = props
  let flexJustify
  switch (buttonsPosition) {
    case 'top-left':
      flexJustify = 'flex-start'
      break
    case 'bottom-left':
      flexJustify = 'flex-start'
      break
    case 'top-centered':
      flexJustify = 'center'
      break
    case 'bottom-centered':
      flexJustify = 'center'
      break
    default:
      flexJustify = 'flex-end'
  }
  return (
    <div style={{ display: 'flex', justifyContent: flexJustify, minHeight: '2rem', margin: buttonsPosition[0] === 't' ? '0 0 .5em' : '.5em 0 0' }}>
      {
        <div className={`actions-container ${actionButtons ? 'show' : ''}`}>
          {
            actionButtons &&
            <div className={'button-container'}>
              <Button
                style={{ minWidth: '2.5rem' }}
                className={'mx-1'}
                type={'success'}
                outlined={formStyles && formStyles.outlined}
                styling={formStyles && formStyles.saveButton}
                label={(formStyles && formStyles.saveButton && formStyles.saveButton.label) || 'Save'}
                onClick={onSave}
                disabled={saveDisabled || !isFormValid}
                icon={formStyles && formStyles.saveButton && formStyles.saveButton.icon}
                iconSize={formStyles && formStyles.saveButton && formStyles.saveButton.iconSize}
              />
              {
                showCancelButton &&
                <Button
                  style={{ minWidth: '2.5rem' }}
                  className={'mx-1'}
                  type={'danger'}
                  outlined={formStyles && formStyles.outlined}
                  styling={formStyles && formStyles.cancelButton}
                  label={(formStyles && formStyles.cancelButton && formStyles.cancelButton.label) || 'Cancel'}
                  onClick={cancelChanges}
                  icon={formStyles && formStyles.cancelButton && formStyles.cancelButton.icon}
                  iconSize={formStyles && formStyles.cancelButton && formStyles.cancelButton.iconSize}
                />
              }
              <style jsx>{`
                .button-container {
                  display: flex;
                }
              `}</style>
            </div>
          }
        </div>
      }
    </div>
  )
}
const Record = PropTypes.object

const color = PropTypes.color
const Icon = PropTypes.Icon

const buttonStyles = {
  icon: Icon,
  iconSize: PropTypes.oneOf(ICON_SIZE),
  buttonColor: color,
  buttonHoverColor: color,
  buttonDisabledColor: color,
  buttonBorderColor: color,
  buttonHoverBorderColor: color,
  buttonDisabledBorderColor: color,
  buttonBorderWidth: PropTypes.string,
  buttonBorderStyle: PropTypes.oneOf(BORDER_STYLES),
  buttonBorderRadius: PropTypes.string,
  buttonPadding: PropTypes.string,
  label: PropTypes.string,
  labelColor: color,
  labelHoverColor: color
}

const theme = {
  formStyles: PropTypes.shape({
    backgroundColor: color,
    headerBackgroundColor: color,
    padding: PropTypes.string,
    borderWidth: PropTypes.string,
    borderColor: color,
    borderStyle: PropTypes.oneOf(BORDER_STYLES),
    borderRadius: PropTypes.string,
    labelAlign: PropTypes.oneOf(ALIGN),
    labelColor: color,
    labelTextTransform: PropTypes.oneOf(TRANSFORM),
    outlinedButtons: PropTypes.bool,
    saveButton: PropTypes.shape(buttonStyles),
    cancelButton: PropTypes.shape(buttonStyles),
    shadow: PropTypes.string
  }),
  fieldStyles: PropTypes.shape({
    allowMultiFieldsGrow: PropTypes.bool,
    labelColor: color,
    labelFontSize: PropTypes.string,
    labelFontWeight: PropTypes.oneOf(WEIGHT),
    labelTextTransform: PropTypes.oneOf(TRANSFORM),
    labelSpacing: PropTypes.string,
    labelOffset: PropTypes.string,
    valueOffset: PropTypes.string,
    backgroundColorEdit: color,
    backgroundColorLocked: color,
    focusedBackgroundColor: color,
    hoverBackgroundColor: color,
    color: color,
    colorOutput: color,
    focusedColor: color,
    hoverColor: color,
    fontStyle: PropTypes.oneOf(STYLES),
    focusedFontStyle: PropTypes.oneOf(STYLES),
    textTransform: PropTypes.oneOf(TRANSFORM),
    borderWidth: PropTypes.string,
    borderColor: color,
    invalidBorderColor: color,
    borderStyle: PropTypes.oneOf(BORDER_STYLES),
    borderRadius: PropTypes.string,
    multiSelectPillColor: color,
    linkColor: color,
    cellPadding: PropTypes.string
  }),
  helpTextTools: PropTypes.shape({
    icon: Icon,
    iconColor: color,
    iconSize: PropTypes.string,
    tooltipColor: color,
    tooltipBackgroundColor: color
  }),
  hintTextStyles: PropTypes.shape({
    color: color,
    fontSize: PropTypes.string,
    align: PropTypes.oneOf(ALIGN),
    padding: PropTypes.string
  })
}

Form.propTypes = {

  formLayout: PropTypes.oneOf(['vertical', 'horizontal']),

  /**
   * Form title
   */
  label: PropTypes.string,

  /**
   * Determines if form header should be displayed
   */
  formHeader: PropTypes.bool,

  /**
   * Action buttons (e.g. Save and Cancel) positions
   */
  buttonsPosition: PropTypes.oneOf(BUTTON_POSITIONS),

  /**
   * Action buttons always visible
   */
  buttonsAlwaysVisible: PropTypes.bool,

  /**
   * Show / Hide cancel button
   */
  showCancelButton: PropTypes.bool,

  /**
   * Spinner
   */
  isLoading: PropTypes.bool,

  /**
   * Determines if the record should be saved on field change or on clicking Save button
   */
  saveOnChange: PropTypes.bool,

  /**
   * Record to be accessed by the component
   */
  record: Record,

  /**
   * Fields to be displayed in the Form component
   */
  fieldset: PropTypes.arrayOf(PropTypes.instanceOf(FieldSet)),

  children: PropTypes.arrayOf(PropTypes.node),

  /**
   * onFormFieldChange(fieldPath, type, value)
   */
  onFormFieldChange: PropTypes.func,

  /**
   * Function to save the record
   */
  save: PropTypes.func,

  /**
   * Number of columns in the form
   */
  columns: PropTypes.number,

  /**
   * Customize toast notifications
   */
  toastMessages: PropTypes.shape({
    showSuccessMessage: PropTypes.bool,
    successMessageText: PropTypes.string,
    showAbortMessage: PropTypes.bool,
    abortMessageText: PropTypes.string,
    showErrorMessage: PropTypes.bool,
    errorMessageText: PropTypes.string,
    includeThrownError: PropTypes.bool
  }),

  /**
   * Row and column gap, in pixels. 1px, 2px, 3px and so on
   */
  gap: PropTypes.string,

  /**
   * Adjust the colors, borders, and alignment
   * */
  styling: PropTypes.shape(theme),

  /**
   * Default new record
   */
  newRecord: Record,

  /**
   * Function to be used for uploading files pasted in rich text editor field
   */
  imageUploadFunc: PropTypes.func,

  /**
   * Path/Folder where the rich text editor pasted files goes
   */
  imageUploadPath: PropTypes.string,

  /**
   * List of things to be used for mentioning
   */
  mentionGroup: PropTypes.array,

  /**
   * Determines which field from the 'mentionGroup' item holds the mention name
   */
  mentionField: PropTypes.string,

  /**
   * Add prompt for redirecting when Form is dirty (has changes)
   */
  preventUnsavedRedirect: PropTypes.bool,

  /**
   * Auto save feedback
   */
  autoSave: PropTypes.shape({
    autoSaveFeedback: PropTypes.bool,
    autoSaveFeedbackPosition: PropTypes.oneOf(['top', 'bottom']),
    autoSaveFeedbackLoaderColor: color
  })
}

Form.defaultProps = {
  isLoading: false,
  formLayout: 'vertical',
  buttonsPosition: 'top-right',
  buttonsAlwaysVisible: false,
  showCancelButton: true,
  formHeader: false,
  preventUnsavedRedirect: false,
  toastMessages: {
    showSuccessMessage: true,
    showAbortMessage: true,
    showErrorMessage: true,
    includeThrownError: false
  },
  autoSave: {
    autoSaveFeedback: true,
    autoSaveFeedbackPosition: 'top'
  },
  styling: {
    formStyles: {
      backgroundColor: 'rgb(249, 249, 251)',
      headerBackgroundColor: 'rgb(211, 211, 211)',
      padding: '.5rem',
      borderStyle: 'none',
      borderRadius: '.25rem',
      labelColor: 'rgb(131, 131, 131)',
      labelTextTransform: 'uppercase',
      outlinedButtons: true,
      shadow: '2px 2px 2px gray'
    },
    fieldStyles: {
      allowMultiFieldsGrow: false,
      labelColor: 'rgb(131, 131, 131)',
      labelFontSize: '.8rem',
      labelTextTransform: 'uppercase',
      cellPadding: '.5rem .2rem',
      colorOutput: 'rgb(131, 131, 131)'
    }
  }
}

const convertToRgba = (rgb, opacity) => {
  if (!rgb) return
  return rgb.replace('rgb', 'rgba').replace(')', `, ${opacity || 1})`)
}

export default Form
